<script setup lang="ts">
import { computed } from 'vue';
import _capitalize from 'lodash/capitalize';

import SimpleLoader from '@/components/loaders/SimpleLoader.vue';

export type BtnColor = 'green' | 'purple' | 'red';

export interface Props {
  color?: BtnColor;
  icon?: string;
  loading?: boolean;
  loaderSize?: number; // TODO refactor to inherit from font-size
}
const props = withDefaults(defineProps<Props>(), {
  loaderSize: undefined,
  color: 'purple',
  icon: 'mdi-star-outline',
  loading: false,
});

const colorClass = computed(() => 'color' + _capitalize(props.color));
</script>

<template>
  <v-btn
    variant="plain"
    density="compact"
    rounded
    size="large"
    min-width="0"
    :ripple="false"
    :class="[
      $style.tableActionBtn,
      $style[colorClass],
      { [$style.loading]: props.loading },
    ]"
    data-testid="tableActionBtn"
  >
    <slot>Action</slot>
    <SimpleLoader
      v-if="props.loading"
      :size="props.loaderSize || 16"
      :width="2"
    />
    <v-icon v-else :icon="props.icon"></v-icon>
  </v-btn>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.tableActionBtn {
  margin: -8px 0;
  padding: 0 variables.$tableActionBtnMarginX;

  :global {
    .v-btn__content {
      text-transform: none;
      letter-spacing: normal;
      font-weight: normal;
      display: flex;
      align-items: center;
      transition: all 0.2s ease-in-out;
    }

    .v-icon,
    .v-progress-circular {
      margin-left: 0.55em;
    }

    .v-icon {
      font-size: inherit;
    }
  }
}

.loading {
  pointer-events: none;
}

.tableActionBtn.colorGreen {
  opacity: 1;
  color: colors.$color-action-success-hovered !important;

  :global {
    .v-btn__content {
      color: colors.$color-action-success-default !important;
    }
  }

  &:hover {
    :global {
      .v-btn__content {
        color: colors.$color-action-success-hovered !important;
      }
    }
  }
  &:active {
    :global {
      .v-btn__content {
        color: colors.$color-action-success-pressed !important;
      }
    }
  }
}

.tableActionBtn.colorPurple {
  opacity: 1;
  color: colors.$color-brand-400 !important;

  :global {
    .v-btn__content {
      color: colors.$color-brand-800 !important;
    }
  }

  &:hover {
    :global {
      .v-btn__content {
        color: colors.$color-brand-400 !important;
      }
    }
  }
  &:active {
    :global {
      .v-btn__content {
        color: colors.$color-brand-300 !important;
      }
    }
  }
}

.tableActionBtn.colorRed {
  opacity: 1;
  color: colors.$color-red-400 !important;

  :global {
    .v-btn__content {
      color: colors.$color-text-primary !important;
    }
  }

  &:hover {
    :global {
      .v-btn__content {
        color: colors.$color-red-400 !important;
      }
    }
  }
  &:active {
    :global {
      .v-btn__content {
        color: colors.$color-red-500 !important;
      }
    }
  }
}

.tableActionBtn:disabled,
.tableActionBtn:disabled:hover {
  :global {
    .v-btn__content {
      color: colors.$color-text-disabled !important;
    }
  }
}
</style>
